import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ApiResponse } from "@shared/types/api";

export type HostEventReviewStatus = "pending" | "approved" | "rejected";

export interface AdminHostEvent {
  id: string;
  slug: string | null;
  name: string;
  description: string | null;
  date: string;
  time: string;
  visibility: string;
  reviewStatus: HostEventReviewStatus;
  types: string[];
  priceCents: number | null;
  capacity: number | null;
  coverImageUrl: string | null;
  source: string | null;
  createdAt: string;
  updatedAt: string;
  ownerId: string;
  ownerFirstName: string | null;
  ownerLastName: string | null;
  ownerEmail: string | null;
  venueName: string;
  venueAddress: string;
  cityName: string | null;
  groupName: string | null;
  groupSlug: string | null;
  rsvpCount: number;
}


export interface CityEventStats {
  cityId: string;
  cityName: string;
  pending: number;
  approved: number;
  rejected: number;
}

export interface HostEventsFilters {
  reviewStatus?: "all" | HostEventReviewStatus;
  search?: string;
  cityId?: string;
  sortBy?: "updatedAt" | "createdAt" | "date" | "name";
  sortDir?: "asc" | "desc";
  limit?: number;
}

type HostEventsApiResponse = ApiResponse<{
  events: AdminHostEvent[];
  counts: { pending: number; approved: number; rejected: number };
  cityStats: CityEventStats[];
}>;

export function useAdminHostEvents(filters: HostEventsFilters = {}) {
  const params = new URLSearchParams();
  if (filters.reviewStatus) params.set("reviewStatus", filters.reviewStatus);
  if (filters.search) params.set("search", filters.search);
  if (filters.cityId && filters.cityId !== "all") params.set("cityId", filters.cityId);
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortDir) params.set("sortDir", filters.sortDir);
  if (filters.limit) params.set("limit", String(filters.limit));
  const qs = params.toString();

  return useQuery<HostEventsApiResponse>({
    queryKey: ["admin-host-events", filters],
    queryFn: (): Promise<HostEventsApiResponse> => api.get(`/admin/host-events${qs ? `?${qs}` : ""}`),
  });
}

export function useReviewHostEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewStatus }: { id: string; reviewStatus: HostEventReviewStatus }) =>
      api.patch(`/admin/host-events/${id}/review`, { reviewStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-host-events"] });
    },
  });
}

export interface EmbeddingBackfillResult {
  total: number;
  succeeded: number;
  failed: number;
}

// Recompute event embeddings. By default only fills in events missing a vector;
// pass { all: true } to recompute every event.
export function useRecomputeEventEmbeddings() {
  return useMutation({
    mutationFn: (opts?: { all?: boolean }): Promise<ApiResponse<EmbeddingBackfillResult>> =>
      api.post(`/admin/host-events/recompute-embeddings`, { all: opts?.all ?? false }),
  });
}
